// Mitschnitt: Was ging wann über den Sender?
//
// Die Seite beantwortet genau eine Frage: „Was lief gestern um 18:40?“ Also erst der
// Kanal, dann der Tag, dann die Stunde – anhören oder herunterladen. Darüber steht, ob
// der Mitschnitt läuft und wie viel Platz er braucht.
//
// Sichtbar nur für Leitung und Moderation; ein- und ausschalten darf die Leitung.

import { useCallback, useEffect, useState } from "react";
import { mitschnittApi, type MitschnittDay, type MitschnittHour, type MitschnittOverview } from "../api";
import { useAuth } from "../auth/AuthProvider";
import { Icon, errorText } from "../components/streaming-ui";
import { Callout, EmptyState, Page, PageHeader, Skeleton, StatTile } from "../components/ui";

function mb(bytes: number): string {
  if (bytes >= 1_000_000_000) return `${(bytes / 1_000_000_000).toFixed(1)} GB`;
  if (bytes >= 1_000_000) return `${Math.round(bytes / 1_000_000)} MB`;
  return `${Math.round(bytes / 1000)} kB`;
}

function mmss(sekunden: number): string {
  const s = Math.max(0, Math.round(sekunden));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

function datum(tag: string): string {
  const [y, m, d] = tag.split("-");
  return `${d}.${m}.${y}`;
}

/** Heute, in der Zeitzone des Senders. */
function heute(zone: string): string {
  try {
    return new Intl.DateTimeFormat("sv-SE", { timeZone: zone }).format(new Date());
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

export function MitschnittPage(): JSX.Element {
  const { user } = useAuth();
  const [data, setData] = useState<MitschnittOverview | null>(null);
  const [slug, setSlug] = useState<string>("");
  const [days, setDays] = useState<MitschnittDay[]>([]);
  const [day, setDay] = useState<string>("");
  const [hours, setHours] = useState<MitschnittHour[]>([]);
  const [playing, setPlaying] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await mitschnittApi.overview();
      setData(d);
      setSlug((s) => s || d.channels[0]?.slug || "");
      setError(null);
    } catch (err) {
      setError(errorText(err));
    }
  }, []);
  useEffect(() => void load(), [load]);

  // Tage des gewählten Kanals nachladen.
  useEffect(() => {
    if (!slug) return;
    mitschnittApi.days(slug)
      .then((r) => {
        setDays(r.days);
        setDay((alt) => (r.days.some((x) => x.day === alt) ? alt : r.days[0]?.day ?? ""));
      })
      .catch((err) => setError(errorText(err)));
  }, [slug]);

  // Stunden des gewählten Tages nachladen.
  useEffect(() => {
    if (!slug || !day) {
      setHours([]);
      return;
    }
    mitschnittApi.hours(slug, day)
      .then((r) => setHours(r.hours))
      .catch((err) => setError(errorText(err)));
  }, [slug, day]);

  if (!data) {
    return (
      <Page>
        {error ? <Callout tone="danger">{error}</Callout> : <div className="space-y-4"><Skeleton className="h-16 w-80" /><Skeleton className="h-40" /></div>}
      </Page>
    );
  }

  const channel = data.channels.find((c) => c.slug === slug);
  const darfAendern = data.may_edit;
  const isAdmin = user?.role === "tenant_admin" || user?.role === "super_admin";

  const speichern = async (input: { enabled?: boolean; keep_days?: number; bitrate_kbps?: number }): Promise<void> => {
    if (!channel) return;
    setBusy(true);
    try {
      await mitschnittApi.save(channel.slug, input);
      await load();
      setError(null);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const tagInfo = days.find((d) => d.day === day);

  return (
    <Page className="max-w-5xl">
      <PageHeader
        eyebrow="Auswerten"
        title="Mitschnitt"
        description="Beschwert sich jemand über etwas, das gestern um 18:40 lief, steht die Stunde hier. Aufgezeichnet wird der Sendeweg – also das, was ohnehin jeder hören konnte. Eine Datei je Stunde, und nach der eingestellten Frist löscht sich der Mitschnitt von selbst."
      />
      <div className="space-y-6">
      {error && <Callout tone="danger">{error}</Callout>}

      {data.channels.length === 0 && (
        <EmptyState title="Es gibt noch keinen Kanal." />
      )}

      {/* Kanal wählen */}
      {data.channels.length > 1 && (
        <div className="tabbar flex-wrap">
          {data.channels.map((c) => (
            <button
              key={c.slug}
              onClick={() => setSlug(c.slug)}
              className={`tab inline-flex items-center gap-2 ${c.slug === slug ? "tab-active" : ""}`}
            >
              {c.name}
              {c.running && <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400"><span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" /> läuft</span>}
            </button>
          ))}
        </div>
      )}

      {channel && (
        <>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <StatTile
            label="Zustand"
            value={<span className="text-lg">{channel.enabled ? (channel.running ? "schneidet mit" : "wartet") : "aus"}</span>}
            tone={channel.enabled ? (channel.running ? "success" : "warning") : "neutral"}
          />
          <StatTile label="Belegt" value={mb(channel.bytes)} hint={`in ${channel.hours} Stunden`} />
          <StatTile label="Aufbewahren" value={channel.keep_days} unit="Tage" hint={channel.oldest ? `Ältestes vom ${datum(channel.oldest.slice(0, 10))}` : undefined} />
          <StatTile label="Bedarf bei voller Frist" value={mb(channel.expected_bytes)} hint="je Kanal" />
        </div>

        <section className="card">
          <div className="card-header flex-wrap">
            <h2 className="card-title">{channel.name}</h2>
            {channel.enabled ? (
              channel.running ? (
                <span className="badge badge-success"><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-current" aria-hidden="true" />schneidet mit</span>
              ) : (
                <span className="badge badge-warning">
                  eingeschaltet, läuft gerade nicht{channel.status !== "active" ? " (Kanal pausiert)" : ""}
                </span>
              )
            ) : (
              <span className="badge badge-neutral">aus</span>
            )}
          </div>
          <div className="card-body space-y-4">

          {channel.last_error && (
            <Callout tone="warning">{channel.last_error}</Callout>
          )}

          <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-3">
            <label className={`flex h-[2.375rem] items-center gap-3 self-end rounded-lg border border-default px-3 ${darfAendern ? "cursor-pointer hover:bg-[var(--bg-hover)]" : ""}`}>
              <input
                type="checkbox"
                disabled={!darfAendern || busy}
                checked={channel.enabled}
                onChange={(e) => void speichern({ enabled: e.target.checked })}
              />
              <span className="font-medium text-primary">Mitschneiden</span>
            </label>

            <label className="block">
              <span className="field-label">Aufbewahren</span>
              <span className="flex items-center gap-2">
                <input
                  type="number"
                  min={data.keep_days_min}
                  max={data.keep_days_max}
                  disabled={!darfAendern || busy}
                  defaultValue={channel.keep_days}
                  onBlur={(e) => {
                    const n = Number(e.target.value);
                    if (n !== channel.keep_days) void speichern({ keep_days: n });
                  }}
                  className="input !w-24 tabular-nums"
                />
                <span className="text-muted">Tage</span>
              </span>
            </label>

            <label className="block">
              <span className="field-label">Qualität</span>
              <select
                disabled={!darfAendern || busy}
                value={channel.bitrate_kbps}
                onChange={(e) => void speichern({ bitrate_kbps: Number(e.target.value) })}
                className="input"
              >
                {data.bitrates.map((b) => (
                  <option key={b} value={b}>{b} kbit/s</option>
                ))}
              </select>
            </label>
          </div>

          <div className="text-sm text-muted">
            Belegt {mb(channel.bytes)} in {channel.hours} Stunden.{" "}
            {channel.oldest && <>Ältestes vom {datum(channel.oldest.slice(0, 10))}. </>}
            Voll ausgeschöpft braucht die Frist rund <strong className="text-secondary">{mb(channel.expected_bytes)}</strong> je Kanal.
          </div>
          {!darfAendern && <p className="text-xs text-muted">Ein- und Ausschalten darf die Leitung.</p>}
          {isAdmin && (
            <p className="text-xs leading-relaxed text-muted">
              Der Mitschnitt zeichnet den Sendeweg auf. Für das Mitschneiden von Telefongesprächen mit Hörern gilt das
              nicht – dafür braucht es deren Einwilligung. Mehr dazu im Handbuch.
            </p>
          )}
          </div>
        </section>
        </>
      )}

      {/* Wiederfinden */}
      {channel && days.length > 0 && (
        <section className="card">
          <div className="card-header flex-wrap">
            <h2 className="card-title">Wiederfinden</h2>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-muted">Tag</span>
              <select value={day} onChange={(e) => setDay(e.target.value)} className="input !h-8 !w-auto">
                {days.map((d) => (
                  <option key={d.day} value={d.day}>
                    {datum(d.day)} · {d.hours} Std · {mb(d.bytes)}
                  </option>
                ))}
              </select>
              {day === heute(data.time_zone) && <span className="badge badge-brand">heute</span>}
              {tagInfo && <span className="hidden text-xs tabular-nums text-muted sm:inline">{tagInfo.hours} Std · {mb(tagInfo.bytes)}</span>}
            </div>
          </div>

          {hours.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-muted">An diesem Tag liegt nichts.</p>
          ) : (
            <ul className="divide-y divide-[var(--border)]">
              {hours.map((h) => (
                <li key={h.id} className={`flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-3 text-sm ${playing === h.id ? "bg-brand-primary/[0.04]" : ""}`}>
                  <span className="flex min-w-0 flex-1 basis-44 items-center gap-2 font-mono tabular-nums text-primary">
                    <Icon name="clock" className="h-4 w-4 text-muted" />
                    {h.label}
                  </span>
                  <span className="w-14 text-right font-mono text-xs tabular-nums text-muted">{mmss(h.seconds)}</span>
                  <span className="w-16 text-right text-xs tabular-nums text-muted">{mb(h.bytes)}</span>
                  {h.running && <span className="badge badge-success">läuft noch</span>}
                  <span className="flex items-center gap-1.5">
                    <button
                      onClick={() => setPlaying(playing === h.id ? null : h.id)}
                      className={`btn btn-sm ${playing === h.id ? "btn-secondary" : "btn-primary"}`}
                    >
                      {playing === h.id ? "Zu" : "Anhören"}
                    </button>
                    <a
                      href={`/api/mitschnitt/${h.id}/datei`}
                      className="btn btn-ghost btn-sm"
                      onClick={(e) => {
                        e.preventDefault();
                        void mitschnittApi.download(h.id, `${channel.slug}-${h.label.replace(/[^0-9]/g, "-")}.mp3`);
                      }}
                    >
                      Herunterladen
                    </a>
                  </span>
                  {playing === h.id && (
                    <div className="w-full pt-1">
                      <MitschnittPlayer id={h.id} />
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
          <p className="border-t border-default px-5 py-3 text-xs text-muted">
            Die Uhrzeiten stehen in der Zeitzone des Senders ({data.time_zone}). Im Player lässt sich springen – für
            18:40 also auf Minute 40 der Stunde 18.
          </p>
        </section>
      )}
      </div>
    </Page>
  );
}

/**
 * Abspielen mit Anmeldung: Ein einfaches <audio src> schickt kein Token mit, darum wird
 * die Datei als Blob geholt. Für eine Stunde MP3 (rund 58 MB) ist das vertretbar.
 */
function MitschnittPlayer({ id }: { id: string }): JSX.Element {
  const [url, setUrl] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    let abgebrochen = false;
    let erzeugt: string | null = null;
    mitschnittApi.blobUrl(id)
      .then((u) => {
        if (abgebrochen) {
          URL.revokeObjectURL(u);
          return;
        }
        erzeugt = u;
        setUrl(u);
      })
      .catch((err) => setFehler(errorText(err)));
    return () => {
      abgebrochen = true;
      if (erzeugt) URL.revokeObjectURL(erzeugt);
    };
  }, [id]);

  if (fehler) return <div className="text-sm text-red-600 dark:text-red-400">{fehler}</div>;
  if (!url) return <div className="text-sm text-muted">Lädt …</div>;
  // eslint-disable-next-line jsx-a11y/media-has-caption
  return <audio controls src={url} className="w-full" />;
}
